import { useMutation } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CopyableText } from "components/CopyableText";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { HarmonizedViolationResolutionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_STAFF_ASSIGNEE,
  SET_HARMONIZED_VIOLATION_GOV_STATUS,
  SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE,
} from "hooks/useHarmonizedViolations";
import { ReactNode, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { assigneeDisplayName, type ViolationRow } from "./columns";
import { SeverityProvenance } from "./SeverityProvenance";
import { ViolationCommentsSection } from "./ViolationCommentsSection";
import {
  formatReportedStatus,
  formatViolationDate,
  isViolationStale,
  ProgramChip,
  REPORTED_STATUS_TOOLTIP,
  SectionHeading,
  StaleChip,
} from "./violationDisplay";
import { ViolationDocumentsSection } from "./ViolationDocumentsSection";

type ResolutionStatusValue = HarmonizedViolationResolutionStatus | "";

/** Narrows an arbitrary select value to a valid resolution status enum (or null). */
function toResolutionStatus(value: string): HarmonizedViolationResolutionStatus | null {
  return Object.values(HarmonizedViolationResolutionStatus).find((s) => s === value) ?? null;
}

/**
 * Editable, user-controlled "Resolution status" — backed by the `govStatus`
 * field, which (unlike EPA's derived `standardizedResolutionStatus`) is
 * preserved across data refreshes, so a user's "Resolved" sticks. EPA's own
 * derived status is shown beside it as read-only context.
 *
 * Mounted with `key={violation.id}` so its local state resets cleanly when a
 * different violation opens (no prop-sync effect). The mutation returns
 * { id, govStatus }; Apollo normalizes by id, so the grid row updates from cache.
 */
function ResolutionStatusEditor({
  violationId,
  initial,
  epaStatus,
  violationEndDate,
  lastSeenAt,
  tenantId,
}: {
  violationId: string;
  initial: ResolutionStatusValue;
  epaStatus: HarmonizedViolationResolutionStatus;
  violationEndDate: string | null | undefined;
  lastSeenAt: string | null | undefined;
  tenantId: string;
}) {
  const alerts = useAlerts();
  const [value, setValue] = useState<ResolutionStatusValue>(initial);
  const [setGovStatusMutation, { loading: saving, error: saveError }] = useMutation(
    SET_HARMONIZED_VIOLATION_GOV_STATUS
  );

  const handleChange = async (raw: string) => {
    const next = toResolutionStatus(raw);
    const previous = value;
    setValue(next ?? "");
    try {
      await setGovStatusMutation({ variables: { tenantId, id: violationId, govStatus: next } });
      alerts.success("Resolution status updated");
    } catch (e) {
      // Roll the field back to its last-saved value so it doesn't show a change
      // that didn't persist.
      setValue(previous);
      alerts.error("Could not save the resolution status. Try again.", e);
    }
  };

  return (
    <Stack gap={0.5}>
      <TextField
        select
        fullWidth
        size="small"
        label="Resolution status"
        value={value}
        disabled={saving}
        error={Boolean(saveError)}
        helperText={saveError ? "Could not save the resolution status. Try again." : undefined}
        onChange={(e) => void handleChange(e.target.value)}
      >
        <MenuItem value="">—</MenuItem>
        {Object.values(HarmonizedViolationResolutionStatus)
          // Not Reported states a gap in EPA's feed, not a triage judgment, so a
          // human cannot assert it manually. Unknown is redundant with leaving
          // the field blank. The API rejects both for the same reasons.
          .filter(
            (status) =>
              status !== HarmonizedViolationResolutionStatus.NotReported &&
              status !== HarmonizedViolationResolutionStatus.Unknown
          )
          .map((status) => (
            <MenuItem key={status} value={status}>
              {prettyPrintEnumValue(status)}
            </MenuItem>
          ))}
      </TextField>
      {/*
        EPA's own status gets its own row rather than the TextField's helperText:
        a save error would otherwise replace it, and FormHelperText renders a <p>
        that can't legally contain the tooltip's IconButton.
      */}
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography variant="caption" color="text.secondary">
          Reported status: {formatReportedStatus(epaStatus, violationEndDate, lastSeenAt)}
        </Typography>
        <InfoIconTooltip
          tooltip={REPORTED_STATUS_TOOLTIP}
          ariaLabel="About reported status"
          iconButtonSx={{ color: (t) => t.palette.grey[400] }}
        />
      </Stack>
    </Stack>
  );
}

/** First selected user id from the single-select picker, or null when cleared. */
function firstUserId(users: UserOption[] | null): string | null {
  return users && users.length > 0 ? (users[0]?.id ?? null) : null;
}

/**
 * Assignee section: a single-select user picker for the per-violation OVERRIDE
 * plus a "set as facility default" control. Mounted with `key={violation.id}` by
 * the drawer so picker state resets when a different violation opens (mirrors
 * GovStatusEditor's keyed-no-effect pattern).
 *
 * The per-violation mutation returns { id, assignee, assigneeIsFacilityDefault }
 * so Apollo normalizes the row by id and the grid updates from cache. The
 * facility-default mutation can change OTHER inherited rows' effective assignee,
 * which Apollo can't infer, so it refetches the list query.
 */
function AssigneeEditor({ violation, tenantId }: { violation: ViolationRow; tenantId: string }) {
  const initialOverride: UserOption[] | null =
    violation.assignee && !violation.assigneeIsFacilityDefault
      ? [
          {
            id: violation.assignee.id,
            email: violation.assignee.email,
            name: assigneeDisplayName(violation.assignee),
          },
        ]
      : null;

  const alerts = useAlerts();
  const [override, setOverride] = useState<UserOption[] | null>(initialOverride);

  const [setAssignee, { loading: savingOverride }] = useMutation(SET_HARMONIZED_VIOLATION_ASSIGNEE);
  const [setFacilityDefault, { loading: savingDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_ASSIGNEE,
    {
      // The grid can't infer which inherited rows changed, so refetch the list.
      refetchQueries: [HARMONIZED_VIOLATIONS],
      awaitRefetchQueries: true,
    }
  );

  const selectedUserId = firstUserId(override);

  const handleOverrideChange = async (users: UserOption[] | null) => {
    setOverride(users);
    try {
      await setAssignee({ variables: { tenantId, id: violation.id, userId: firstUserId(users) } });
      alerts.success(firstUserId(users) ? "Assignee updated" : "Assignee override cleared");
    } catch (e) {
      alerts.error("Could not save the assignee. Try again.", e);
    }
  };

  const handleSetDefault = async () => {
    // The button is disabled without a selection; guard anyway for safety.
    if (!selectedUserId) return;
    try {
      await setFacilityDefault({
        variables: { tenantId, facilityId: violation.facility.id, userId: selectedUserId },
      });
      alerts.success(`Default assignee set for ${violation.facility.name}`);
    } catch (e) {
      alerts.error("Could not set the facility default assignee. Try again.", e);
    }
  };

  const effectiveLabel = violation.assignee
    ? assigneeDisplayName(violation.assignee)
    : "Unassigned";

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Assignee</Typography>
      <MultipleUserPicker
        label="Assignee (override)"
        multiple={false}
        value={override}
        disabled={savingOverride}
        onChange={(users) => void handleOverrideChange(users)}
      />
      <Typography variant="caption" color="text.secondary">
        {violation.assigneeIsFacilityDefault
          ? `Inherited from the facility default: ${effectiveLabel}`
          : `Effective assignee: ${effectiveLabel}`}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={savingDefault || !selectedUserId}
        onClick={() => void handleSetDefault()}
        sx={{ alignSelf: "flex-start" }}
      >
        Set as default for {violation.facility.name}
      </Button>
      {!selectedUserId ? (
        <Typography variant="caption" color="text.secondary">
          Select an assignee above to set it as this facility's default.
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * Staff-only counterpart to {@link AssigneeEditor}: a single-select STAFF user
 * picker for the per-violation staff OVERRIDE plus a "set as staff default"
 * control. Backed by `staffUsers` (Encamp employees) via the picker's
 * `staffOnly` mode — the backend mutations reject non-staff target users.
 *
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens. The per-violation mutation returns
 * { id, staffAssignee, staffAssigneeIsFacilityDefault } so Apollo normalizes the
 * row by id; the facility-default mutation can change OTHER inherited rows, so
 * it refetches the list query. Rendered only for staff callers.
 */
function StaffAssigneeEditor({
  violation,
  tenantId,
}: {
  violation: ViolationRow;
  tenantId: string;
}) {
  const initialOverride: UserOption[] | null =
    violation.staffAssignee && !violation.staffAssigneeIsFacilityDefault
      ? [
          {
            id: violation.staffAssignee.id,
            email: violation.staffAssignee.email,
            name: assigneeDisplayName(violation.staffAssignee),
          },
        ]
      : null;

  const alerts = useAlerts();
  const [override, setOverride] = useState<UserOption[] | null>(initialOverride);

  const [setStaffAssignee, { loading: savingOverride }] = useMutation(
    SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEE
  );
  const [setFacilityDefault, { loading: savingDefault }] = useMutation(
    SET_HARMONIZED_VIOLATION_FACILITY_DEFAULT_STAFF_ASSIGNEE,
    {
      // The grid can't infer which inherited rows changed, so refetch the list.
      refetchQueries: [HARMONIZED_VIOLATIONS],
      awaitRefetchQueries: true,
    }
  );

  const selectedUserId = firstUserId(override);

  const handleOverrideChange = async (users: UserOption[] | null) => {
    setOverride(users);
    try {
      await setStaffAssignee({
        variables: { tenantId, id: violation.id, userId: firstUserId(users) },
      });
      alerts.success(
        firstUserId(users) ? "Staff assignee updated" : "Staff assignee override cleared"
      );
    } catch (e) {
      alerts.error("Could not save the staff assignee. Try again.", e);
    }
  };

  const handleSetDefault = async () => {
    // The button is disabled without a selection; guard anyway for safety.
    if (!selectedUserId) return;
    try {
      await setFacilityDefault({
        variables: { tenantId, facilityId: violation.facility.id, userId: selectedUserId },
      });
      alerts.success(`Default staff assignee set for ${violation.facility.name}`);
    } catch (e) {
      alerts.error("Could not set the facility default staff assignee. Try again.", e);
    }
  };

  const effectiveLabel = violation.staffAssignee
    ? assigneeDisplayName(violation.staffAssignee)
    : "Unassigned";

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Staff assignee</Typography>
      <MultipleUserPicker
        label="Staff assignee (override)"
        multiple={false}
        staffOnly
        value={override}
        disabled={savingOverride}
        onChange={(users) => void handleOverrideChange(users)}
      />
      <Typography variant="caption" color="text.secondary">
        {violation.staffAssigneeIsFacilityDefault
          ? `Inherited from the facility staff default: ${effectiveLabel}`
          : `Effective staff assignee: ${effectiveLabel}`}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={savingDefault || !selectedUserId}
        onClick={() => void handleSetDefault()}
        sx={{ alignSelf: "flex-start" }}
      >
        Set as staff default for {violation.facility.name}
      </Button>
      {!selectedUserId ? (
        <Typography variant="caption" color="text.secondary">
          Select a staff assignee above to set it as this facility's default.
        </Typography>
      ) : null}
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * The read-only EPA summary: a two-column grid of fields (Field returns null
 * for empty values, so gaps close up), the co-located sites list, and the two
 * external ECHO report links. Facility identity leads.
 */
function ViolationDetailsSection({
  violation,
  tenantId,
}: {
  violation: ViolationRow;
  tenantId: string;
}) {
  return (
    <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 3,
          rowGap: 2,
        }}
      >
        <Field
          label="EPA facility ID"
          value={
            violation.epaFacilityId ? <CopyableText>{violation.epaFacilityId}</CopyableText> : null
          }
        />
        <Field
          label="Registry ID"
          value={violation.registryId ? <CopyableText>{violation.registryId}</CopyableText> : null}
        />
        <Field label="State" value={violation.state} />
        <Field label="Agency" value={violation.violationAgency} />
        <Field label="Agency level" value={violation.violationAgencyLevel} />
        <Field label="Violation code" value={violation.violationCode} />
        <Field label="Pollutant" value={violation.pollutantTypeDesc} />
        <Field label="Citations" value={violation.citations} />
        <Field label="Citation descriptions" value={violation.citationDescs} />
        <Field label="Resolution method" value={violation.resolutionMethodDesc} />
        <Field label="Schedule event" value={violation.scheduleEventDesc} />
        <Field label="Started" value={formatViolationDate(violation.violationStartDate)} />
        <Field label="Ended" value={formatViolationDate(violation.violationEndDate)} />
        <Field label="Last seen" value={formatViolationDate(violation.lastSeenAt)} />
      </Box>

      {violation.siteCount > 1 ? (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Affected sites ({violation.siteCount})</Typography>
          {violation.affectedFacilities.map((f) => (
            <Link
              key={f.id}
              component={RouterLink}
              to={`/o/${tenantId}/facilities/${f.id}/profile`}
              target="_blank"
              rel="noopener"
              variant="body2"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {f.name} <OpenInNew fontSize="inherit" />
            </Link>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {violation.detailLink ? (
          <Button
            component="a"
            href={violation.detailLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNew />}
          >
            Violation Report in Echo
          </Button>
        ) : null}
        {violation.epaFacilityLink ? (
          <Button
            component="a"
            href={violation.epaFacilityLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNew />}
          >
            Facility Report in Echo
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function ViolationDetailDrawer({
  violation,
  tenantId,
  refreshStartedAt,
  onClose,
}: {
  violation: ViolationRow | null;
  tenantId: string;
  /** Start of the latest successful refresh — used to flag a stale violation. */
  refreshStartedAt?: string | null;
  onClose: () => void;
}) {
  const { isStaff } = useCurrentUser();
  if (!violation) {
    // No violation selected: render nothing. A closed RightDrawerShell would
    // still leave a drawer paper in the DOM, because the shell sets
    // ModalProps={{ keepMounted: true }}.
    return null;
  }

  const stale = isViolationStale(violation.lastSeenAt, refreshStartedAt);
  const facilityProfilePath = `/o/${tenantId}/facilities/${violation.facility.id}/profile`;

  return (
    <RightDrawerShell
      open
      onClose={onClose}
      // Widened over the default so the two-column details grid breathes.
      width={{ md: 800 }}
      title={
        <Typography variant="h6">
          Violation{" "}
          <Typography component="span" variant="inherit" fontWeight="bold">
            {violation.violationId}
          </Typography>
        </Typography>
      }
    >
      <Box role="region" aria-label="Violation detail">
        {/* Violation title and facility read as subheads with consistent type —
            they are the two things a reader orients on. */}
        <Box sx={{ mb: 2 }}>
          {violation.violationDescription ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {violation.violationDescription}
            </Typography>
          ) : null}
          <Link
            component={RouterLink}
            to={facilityProfilePath}
            target="_blank"
            rel="noopener"
            variant="subtitle1"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
          >
            {violation.facility.name} <OpenInNew fontSize="inherit" />
          </Link>
        </Box>

        {/* useFlexGap makes spacing a real CSS gap on both axes — without it a
            row Stack only spaces horizontally, so the wide escalation chips
            sat flush against the chip row above them whenever they wrapped. */}
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          alignItems="center"
          sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
        >
          {/* Each chip carries its own hover explanation, so there are no separate
              info icons in this row — the chip is the hover target. */}
          <ProgramChip program={violation.sourceProgram} />
          {stale ? <StaleChip /> : null}
        </Stack>

        {/* Everything about "why this severity" lives in one block — level,
            ranked reasons, and the old-signal caution — replacing the chip
            tooltip, escalation chips, and Permit status since field. */}
        <Box sx={{ mb: 3 }}>
          <SeverityProvenance
            severity={violation.standardizedSeverity}
            determinantDesc={violation.stdSeverityDeterminantDesc}
            determinantCode={violation.stdSeverityDeterminantCode}
            orderStatuteCode={violation.orderStatuteCode}
            permitTerminalDate={violation.permitTerminalDate}
            now={new Date()}
          />
        </Box>

        <SectionHeading>Details</SectionHeading>
        <ViolationDetailsSection violation={violation} tenantId={tenantId} />

        <SectionHeading>Tracking</SectionHeading>
        <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
          <ResolutionStatusEditor
            key={`status-${violation.id}`}
            violationId={violation.id}
            initial={violation.govStatus ?? ""}
            epaStatus={violation.standardizedResolutionStatus}
            violationEndDate={violation.violationEndDate}
            lastSeenAt={violation.lastSeenAt}
            tenantId={tenantId}
          />
          <AssigneeEditor
            key={`assignee-${violation.id}`}
            violation={violation}
            tenantId={tenantId}
          />
          {isStaff ? (
            <StaffAssigneeEditor
              key={`staff-assignee-${violation.id}`}
              violation={violation}
              tenantId={tenantId}
            />
          ) : null}
          {violation.lastEditedBy ? (
            <Typography variant="caption" color="text.secondary">
              Last edited by {assigneeDisplayName(violation.lastEditedBy)} on{" "}
              {formatViolationDate(violation.lastEditedAt)}
            </Typography>
          ) : null}
        </Stack>

        <ViolationCommentsSection
          key={`comments-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          facilityId={violation.facility.id}
        />

        <ViolationDocumentsSection
          key={`documents-${violation.id}`}
          tenantId={tenantId}
          violationId={violation.id}
          facilityId={violation.facility.id}
        />
      </Box>
    </RightDrawerShell>
  );
}
